import { useMemo, useEffect, useState } from "react";
import ReactEChartsCore from "echarts-for-react/lib/core";
import * as echarts from "echarts/core";
import { TreeChart } from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DatasetComponent,
} from "echarts/components";
import { SVGRenderer } from "echarts/renderers";
import { useNavigate } from "react-router-dom";
import { getUnixTime, sub } from "date-fns";
import getSiteTreeNodeOptions from "../functions/getSiteTreeNodeOptions";
import type {
  UserSitesConnection,
  UserSitesNode,
  ParsedSiteFeed,
  SiteManagerChargerDetails,
} from "../types";
import type { ECharts } from "echarts";

const LABELS = {
  site: "Site",
  mainBoard: "Main Board",
  subBoard: "Sub Board",
  circuit: "Circuit",
  charger: "Charger",
  amps: "A",
  maxOutput: "Max output",
  sessionLimit: "Session limit",
  unknown: "Unknown",
};

type SiteTreeProps = {
  siteData: UserSitesConnection<ParsedSiteFeed[]>["sites"] | undefined;
  chargerOnlineStatus: Array<{ serial: string; last_comms: string }> | undefined;
  chargerDetails: SiteManagerChargerDetails[] | undefined;
  siteTreeRef: React.RefObject<ReactEChartsCore>;
  siteTreeCardContainerRef: React.RefObject<HTMLDivElement>;
  isSmallToMediumScreen: boolean;
  isConfigureMenuOpen: boolean;
  selectedNodeToConfigure: { type: string; name: string } | null;
  setSelectedNodeToConfigure: (node: { type: string; name: string } | null) => void;
};

export default function SiteTree({
  siteData,
  chargerOnlineStatus,
  chargerDetails,
  siteTreeRef,
  siteTreeCardContainerRef,
  isSmallToMediumScreen,
  isConfigureMenuOpen,
  selectedNodeToConfigure,
  setSelectedNodeToConfigure,
}: SiteTreeProps) {
  const navigate = useNavigate();
  const [animationComplete, setAnimationComplete] = useState(false);
  const [initialiseOverflowScrollPosition, setInitialiseOverflowScrollPosition] = useState(false);

  function isChargerOnline(serial: string, arr: Array<{ serial: string; last_comms: string }> | undefined): boolean {
    const lastComms = arr?.find((s) => s.serial === serial)?.last_comms;
    if (lastComms == null || lastComms.trim() === "" || isNaN(new Date(lastComms.trim()).getTime())) return false;
    return getUnixTime(new Date(lastComms.trim())) > getUnixTime(sub(new Date(), { minutes: 15 }));
  }

  const countOfNodes = useMemo(() => {
    let count = 0;
    if (siteData != null) {
      count += 1;
      count += siteData?.edges?.[0]?.node?.feeds?.length ?? 0;
      siteData?.edges?.[0]?.node?.feeds?.forEach((mainBoard) => {
        count += mainBoard?.feeds?.length ?? 0;
        mainBoard?.feeds?.forEach((subBoard) => {
          count += subBoard?.feeds?.length ?? 0;
          subBoard?.feeds?.forEach((circuit) => {
            count += circuit?.feeds?.length ?? 0;
          });
        });
      });
    }
    return count;
  }, [siteData]);

  const siteTreeChartOptions = useMemo(() => {
    function generateSiteTreeData(siteDataNode: UserSitesNode<ParsedSiteFeed[]>) {
      const siteTreeData = getSiteTreeNodeOptions({
        nodeType: "site",
        nodeLabel: siteDataNode?.name ?? LABELS.site,
        hasCurrentMeter: false,
      });

      const feeds = Array.isArray(siteDataNode?.feeds) ? siteDataNode.feeds : [];
      feeds.forEach((mainBoard) => {
        const mainBoardData = getSiteTreeNodeOptions({
          nodeType: "mainDistBoard",
          nodeLabel: mainBoard.label ?? LABELS.mainBoard,
          hasCurrentMeter: (mainBoard?.hasPoint?.length ?? 0) > 0,
          nodeDescription: `${LABELS.maxOutput} = ${mainBoard?.ratedMaximumVoltageOutput?.integerValue ?? LABELS.unknown} ${LABELS.amps}`,
          isSmallToMediumScreen,
        });

        const subBoards = mainBoard?.feeds?.filter((f) => (f as ParsedSiteFeed).type?.includes("subDistBoard")) ?? [];

        if (subBoards.length > 0) {
          subBoards.forEach((subBoard) => {
            const sub = subBoard as ParsedSiteFeed;
            const breakerPanelData = getSiteTreeNodeOptions({
              nodeType: "subDistBoard",
              nodeLabel: sub.label ?? LABELS.subBoard,
              hasCurrentMeter: (sub?.hasPoint?.length ?? 0) > 0,
              powerTagLabel: sub?.hasPoint?.[0]?.serial,
              nodeDescription: `${LABELS.maxOutput} = ${sub?.ratedMaximumVoltageOutput?.integerValue ?? LABELS.unknown} ${LABELS.amps}`,
            });

            const circuits = (sub?.feeds ?? []).filter((f) => (f as ParsedSiteFeed).type?.includes("circuit"));
            if (circuits.length > 0) {
              circuits.forEach((circuit) => {
                const c = circuit as ParsedSiteFeed;
                const circuitData = getSiteTreeNodeOptions({
                  nodeType: "circuit",
                  nodeLabel: c.label ?? LABELS.circuit,
                  hasCurrentMeter: false,
                });
                const chargers = (c?.feeds ?? []).filter((f) => (f as ParsedSiteFeed).type?.includes("charger"));
                chargers.forEach((charger) => {
                  const ch = charger as ParsedSiteFeed;
                  const cd = chargerDetails?.find((d) => d.serial === ch.label);
                  const chargerData = getSiteTreeNodeOptions({
                    nodeType: "charger",
                    nodeLabel: cd?.name ?? ch.label ?? LABELS.charger,
                    hasCurrentMeter: false,
                    deviceModel: cd?.model,
                    nodeDescription: `${LABELS.sessionLimit} ${cd?.session_limit ?? cd?.circuit_amp_limit ?? LABELS.unknown} ${LABELS.amps}`,
                    isOnline: isChargerOnline(ch.label ?? "", chargerOnlineStatus),
                    isCharging: cd?.isCharging ?? false,
                    isSmallToMediumScreen,
                  });
                  circuitData.children.push(chargerData);
                });
                breakerPanelData.children.push(circuitData);
              });
            } else {
              const chargers = (sub?.feeds ?? []).filter((f) => (f as ParsedSiteFeed).type?.includes("charger"));
              chargers.forEach((charger) => {
                const ch = charger as ParsedSiteFeed;
                const cd = chargerDetails?.find((d) => d.serial === ch.label);
                const chargerData = getSiteTreeNodeOptions({
                  nodeType: "charger",
                  nodeLabel: cd?.name ?? ch.label ?? LABELS.charger,
                  hasCurrentMeter: false,
                  deviceModel: cd?.model,
                  nodeDescription: `${LABELS.sessionLimit} ${cd?.session_limit ?? cd?.circuit_amp_limit ?? LABELS.unknown} ${LABELS.amps}`,
                  isOnline: isChargerOnline(ch.label ?? "", chargerOnlineStatus),
                  isCharging: cd?.isCharging ?? false,
                  isSmallToMediumScreen,
                });
                breakerPanelData.children.push(chargerData);
              });
            }
            mainBoardData.children.push(breakerPanelData);
          });
        } else {
          const circuits = (mainBoard?.feeds ?? []).filter((f) => (f as ParsedSiteFeed).type?.includes("circuit"));
          if (circuits.length > 0) {
            circuits.forEach((circuit) => {
              const c = circuit as ParsedSiteFeed;
              const circuitData = getSiteTreeNodeOptions({
                nodeType: "circuit",
                nodeLabel: c.label ?? LABELS.circuit,
                hasCurrentMeter: false,
              });
              const chargers = (c?.feeds ?? []).filter((f) => (f as ParsedSiteFeed).type?.includes("charger"));
              chargers.forEach((charger) => {
                const ch = charger as ParsedSiteFeed;
                const cd = chargerDetails?.find((d) => d.serial === ch.label);
                const chargerData = getSiteTreeNodeOptions({
                  nodeType: "charger",
                  nodeLabel: cd?.name ?? ch.label ?? LABELS.charger,
                  hasCurrentMeter: false,
                  deviceModel: cd?.model,
                  nodeDescription: `${LABELS.sessionLimit} ${cd?.session_limit ?? cd?.circuit_amp_limit ?? LABELS.unknown} ${LABELS.amps}`,
                  isOnline: isChargerOnline(ch.label ?? "", chargerOnlineStatus),
                  isCharging: cd?.isCharging ?? false,
                  isSmallToMediumScreen,
                });
                circuitData.children.push(chargerData);
              });
              mainBoardData.children.push(circuitData);
            });
          } else {
            const chargers = (mainBoard?.feeds ?? []).filter((f) => (f as ParsedSiteFeed).type?.includes("charger"));
            chargers.forEach((charger) => {
              const ch = charger as ParsedSiteFeed;
              const cd = chargerDetails?.find((d) => d.serial === ch.label);
              const chargerData = getSiteTreeNodeOptions({
                nodeType: "charger",
                nodeLabel: cd?.name ?? ch.label ?? LABELS.charger,
                hasCurrentMeter: false,
                deviceModel: cd?.model,
                nodeDescription: `${LABELS.sessionLimit} ${cd?.session_limit ?? cd?.circuit_amp_limit ?? LABELS.unknown} ${LABELS.amps}`,
                isOnline: isChargerOnline(ch.label ?? "", chargerOnlineStatus),
                isCharging: cd?.isCharging ?? false,
                isSmallToMediumScreen,
              });
              mainBoardData.children.push(chargerData);
            });
          }
        }
        siteTreeData.children.push(mainBoardData);
      });

      return siteTreeData;
    }

    return {
      tooltip: { show: false },
      series: [
        {
          type: "tree",
          left: isSmallToMediumScreen ? "65px" : "6%",
          right: isSmallToMediumScreen ? "120px" : "180px",
          top: "2%",
          bottom: "2%",
          initialTreeDepth: isSmallToMediumScreen ? 1 : 3,
          symbol: "circle",
          symbolSize: [70, 70],
          itemStyle: { color: "#ffffff", borderWidth: 3, borderColor: "#CBCBCB" },
          label: {
            color: "#1a1a1a",
            padding: [8, 8],
            fontSize: 14,
            offset: [0, 8],
            position: "inside",
            rich: {
              name: { color: "#1a1a1a", fontWeight: "bold", fontSize: 15, align: "center", padding: [0, 0, 10, 0] },
              description: { height: 30, width: 50, lineHeight: 40, fontSize: 13, fontWeight: "bold", color: "#666666", align: "center" },
              graphic: { align: "center" },
            },
          },
          lineStyle: { color: "#666666" },
          emphasis: { focus: "descendant" },
          expandAndCollapse: false,
          data: [siteData != null ? generateSiteTreeData(siteData.edges[0].node) : []],
          selectedMode: isConfigureMenuOpen,
        },
      ],
    };
  }, [chargerDetails, chargerOnlineStatus, isConfigureMenuOpen, isSmallToMediumScreen, siteData]);

  echarts.use([TitleComponent, TooltipComponent, LegendComponent, GridComponent, DatasetComponent, TreeChart, SVGRenderer]);

  useEffect(() => {
    const chart = siteTreeRef.current?.getEchartsInstance();
    if (!animationComplete) chart?.on("finished", () => setAnimationComplete(true));
  }, [animationComplete, siteTreeRef]);

  useEffect(() => {
    const chart: ECharts | undefined = siteTreeRef.current?.getEchartsInstance();
    if (!animationComplete) return;
    const handler = (params: { value?: string; name?: string; seriesId?: string; dataIndex?: number; collapsed?: boolean }) => {
      const value = params.value;
      const name = params.name ?? "";
      if (!["site", "mainDistBoard", "subDistBoard", "charger", "circuit"].includes(value ?? "")) return;
      if (!isConfigureMenuOpen) {
        chart?.dispatchAction({ type: "treeExpandAndCollapse", seriesId: params.seriesId!, dataIndex: params.dataIndex! });
        if (value === "charger") {
          const serial = chargerDetails?.find((cd) => cd.name === name)?.serial ?? chargerDetails?.find((cd) => cd.serial === name)?.serial;
          if (serial != null && serial !== "") navigate(`/c/${serial}`);
        }
      } else {
        if (params.collapsed === true) {
          chart?.dispatchAction({ type: "treeExpandAndCollapse", seriesId: params.seriesId!, dataIndex: params.dataIndex! });
        }
        if (selectedNodeToConfigure?.name === name && params.collapsed === false) {
          chart?.dispatchAction({ type: "treeExpandAndCollapse", seriesId: params.seriesId!, dataIndex: params.dataIndex! });
          setSelectedNodeToConfigure(null);
        } else {
          setSelectedNodeToConfigure({ type: value ?? "node", name });
        }
      }
    };
    chart?.on("click", "series.tree.data", handler as (params: unknown) => void);
    return () => { chart?.off("click"); };
  }, [isConfigureMenuOpen, siteTreeRef, selectedNodeToConfigure, setSelectedNodeToConfigure, navigate, animationComplete, chargerDetails]);

  useEffect(() => {
    if (isSmallToMediumScreen && !initialiseOverflowScrollPosition && siteTreeCardContainerRef?.current?.scrollLeft === 0) {
      siteTreeCardContainerRef.current.scrollLeft = 800;
      setInitialiseOverflowScrollPosition(true);
    }
  }, [initialiseOverflowScrollPosition, isSmallToMediumScreen, siteTreeCardContainerRef]);

  return (
    <div
      id="SiteTree"
      style={isSmallToMediumScreen ? { minWidth: "580px" } : { maxWidth: "82vw" }}
      data-animation-complete={!animationComplete}
    >
      <ReactEChartsCore
        echarts={echarts}
        ref={siteTreeRef}
        option={siteTreeChartOptions}
        style={{
          width: "100%",
          height: isSmallToMediumScreen ? `${200 + Math.max(400, 80 * countOfNodes)}px` : `${Math.max(600, 80 * countOfNodes)}px`,
        }}
        opts={{ renderer: "svg" }}
      />
    </div>
  );
}
